import { Button, ButtonProps, GroupBox } from '@infotech-soe/erp-ui-kit';
import { DatasetStyleRuleCondition } from '@infotech-soe/metaschema';
import { produce } from 'immer';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { DatasetPresetRule } from '../../../../../models/user-config/DisplayModeConfig';
import { generateDefaultCondition } from '../../../../../services/dataset/datasetConfigUtils';
import { DatasetGridColumn } from '../../../../../services/dataset/view-model/DatasetGridColumn';

import CellFormatter from './CellFormatter';
import InputConditions from './InputConditions';
import { messages } from './messages';
import { TColorInputName, ValueTypes } from './types';

const ADD_CONDITION = 'add-condition';
const DELETE_CONDITION = 'delete-condition';

interface Props {
  column: DatasetGridColumn;
  rule: DatasetPresetRule;
  onConfirm: (key: string, conditions: DatasetStyleRuleCondition[]) => void;
}

const RuleEditor = ({ column, rule, onConfirm }: Props): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const [ruleConditions, setRuleConditions] = useState(rule.conditions);
  const skipExternalSync = useRef(false);

  useEffect(() => {
    skipExternalSync.current = true;
    onConfirm(column.field, ruleConditions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleConditions, column.field]);

  useEffect(() => {
    if (skipExternalSync.current) {
      skipExternalSync.current = false;
      return;
    }
    setRuleConditions(rule.conditions);
  }, [rule.conditions]);

  const handleOnColorChange = useCallback(
    (ruleIndex: number) => (newValue: string | null, name: TColorInputName) => {
      setRuleConditions(prev =>
        produce(prev, (draftState: any[]) => {
          draftState[ruleIndex].color[name] = newValue;
        })
      );
    },
    []
  );

  const handleChange = useCallback(
    (ruleIndex: number) => (newValue: ValueTypes | null, ruleName: string) => {
      setRuleConditions(prev =>
        produce(prev, (draftState: DatasetStyleRuleCondition[]) => {
          draftState[ruleIndex].value[ruleName] = newValue ?? null;
        })
      );
    },
    []
  );

  const handleClick = useCallback(
    (index: number): ButtonProps['onClick'] =>
      (_e, n) => {
        const updatedRuleState = [...ruleConditions];

        if (n === DELETE_CONDITION) updatedRuleState.splice(index, 1);
        else if (n === ADD_CONDITION)
          updatedRuleState.push(generateDefaultCondition(column.domain.type));

        setRuleConditions(updatedRuleState);
      },
    [column.domain.type, ruleConditions]
  );

  return (
    <>
      {ruleConditions.map(
        (condition: DatasetStyleRuleCondition, index: number) => (
          <GroupBox
            // eslint-disable-next-line react/no-array-index-key
            key={column.field + index}
            className="m-ma-sm"
          >
            <GroupBox.Content className="column">
              <InputConditions
                column={column}
                condition={condition}
                onChange={handleChange(index)}
              />
              <CellFormatter
                condition={condition}
                onChange={handleChange(index)}
                onColorChange={handleOnColorChange(index)}
              />
              {index > 0 && (
                <Button
                  className="m-mt-md self-end"
                  name={DELETE_CONDITION}
                  label={formatMessage(messages.deleteCondition)}
                  onClick={handleClick(index)}
                />
              )}
            </GroupBox.Content>
          </GroupBox>
        )
      )}
      <Button
        className="m-mt-md m-ml-sm"
        name={ADD_CONDITION}
        label={formatMessage(messages.addCondition)}
        onClick={handleClick(-1)}
      />
    </>
  );
};
export default memo(RuleEditor);
